import {
  Box,
  Container,
  SpaceBetween,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React from 'react';

const MAX_ROWS = 2;

const tile = (bg) => ({
  background: bg,
  borderRadius: 10,
  padding: '10px 12px',
});

const monoRow = {
  fontFamily: 'Monaco, monospace',
  fontSize: 12,
  lineHeight: 1.9,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
};

function truncate(id) {
  return id.length > 13 ? `${id.slice(0, 13)}…` : id;
}

function DiscrepancyList({
  heading, headingColor, items, renderAction,
}) {
  if (items.length === 0) return null;
  const shown = items.slice(0, MAX_ROWS);
  const overflow = items.length - shown.length;
  return (
    <div>
      <Box fontSize="body-s" fontWeight="bold" color={headingColor}>
        {`${heading} (${items.length})`}
      </Box>
      {shown.map((item) => (
        <div key={item.id} style={monoRow}>
          <span style={{ color: 'rgb(66,70,80)' }}>{truncate(item.id)}</span>
          {renderAction(item)}
        </div>
      ))}
      {overflow > 0 ? (
        <Box fontSize="body-s" color="text-status-inactive" fontFamily="monospace">
          {`+${overflow} more`}
        </Box>
      ) : null}
    </div>
  );
}

DiscrepancyList.propTypes = {
  heading: PropTypes.string.isRequired,
  headingColor: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  renderAction: PropTypes.func.isRequired,
};

export default function Discrepancies({ missing, unexpected, otherSku }) {
  return (
    <Container>
      <SpaceBetween size="m">
        <Box variant="h3">Discrepancies</Box>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div style={tile('rgb(255,245,245)')}>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
              {missing.length}
            </Box>
            <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">Missing</Box>
          </div>
          <div style={tile('rgb(240,251,255)')}>
            <Box fontSize="heading-l" fontWeight="bold" color="text-status-info">
              {unexpected.length}
            </Box>
            <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">Unexpected</Box>
          </div>
          <div style={tile('rgb(246,246,249)')}>
            <Box fontSize="heading-l" fontWeight="bold" color="text-body-secondary">
              {otherSku.length}
            </Box>
            <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">Other SKU</Box>
          </div>
        </div>

        <DiscrepancyList
          heading="Missing in physical"
          headingColor="text-status-error"
          items={missing}
          renderAction={() => (
            <Box variant="span" color="text-status-error" fontSize="body-s">scrap −1</Box>
          )}
        />
        <DiscrepancyList
          heading="Unexpected in physical"
          headingColor="text-status-warning"
          items={unexpected}
          renderAction={() => (
            <Box variant="span" color="text-status-success" fontSize="body-s">add +1</Box>
          )}
        />
        <DiscrepancyList
          heading="Different item type"
          headingColor="text-body-secondary"
          items={otherSku}
          renderAction={(item) => (
            <Box variant="span" color="text-status-inactive" fontSize="body-s">
              {item.otherSkuLabel}
            </Box>
          )}
        />

        <Box fontSize="body-s" color="text-status-inactive">
          Committing reconciles on-hand and records an inventory adjustment transaction.
        </Box>
      </SpaceBetween>
    </Container>
  );
}

Discrepancies.propTypes = {
  missing: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  unexpected: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  otherSku: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    otherSkuLabel: PropTypes.string,
  })).isRequired,
};
